import { Grid, Paper, Skeleton } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import SearchField from 'src/shared/components/form/searchField';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { useGlobals } from 'src/shared/contexts/appContext';
import { Association } from 'src/shared/models/association/association';
import { District } from 'src/shared/models/district/district';
import { AssociationAndDistrictSelect } from 'src/shared/components/organisms/associationAndDistrictSelect';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { MemberProfileGroupSearchFilter } from '../models/memberProfileGroupSearchFilter';
import { MemberProfileGroupDataGrid } from '../components/memberProfileGroupDataGrid';

export const MemberProfileGroupPage = () => {
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();

  const [association, setAssociation] = useState<Association>();
  const [district, setDistrict] = useState<District>();
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  const [memberProfileGroupSearchFilter, setMemberProfileGroupSearchFilter] = useLocalStorage(LocalStorageKeys.MemberProfileGroupSearchFilter, {
    sortCol: 'title',
    // Stigande, annars listas profilgrupperna Ö till A. API:et läser sortDir sedan 6772.
    sortDir: 'asc',
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
  } as MemberProfileGroupSearchFilter);

  const searchFilter: MemberProfileGroupSearchFilter = useMemo(() => {
    return {
      filter: memberProfileGroupSearchFilter?.filter,
      pageSize: memberProfileGroupSearchFilter?.pageSize,
      pageIndex: memberProfileGroupSearchFilter?.pageIndex,
      sortCol: memberProfileGroupSearchFilter?.sortCol,
      sortDir: memberProfileGroupSearchFilter?.sortDir,
      districtId: memberProfileGroupSearchFilter?.districtId,
      associationId: memberProfileGroupSearchFilter?.associationId,
      skipAccessFilter: true,
    } as MemberProfileGroupSearchFilter;
  }, [
    memberProfileGroupSearchFilter?.filter,
    memberProfileGroupSearchFilter?.pageIndex,
    memberProfileGroupSearchFilter?.pageSize,
    memberProfileGroupSearchFilter?.sortCol,
    memberProfileGroupSearchFilter?.sortDir,
    memberProfileGroupSearchFilter?.districtId,
    memberProfileGroupSearchFilter?.associationId,
  ]);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setMemberProfileGroupSearchFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setMemberProfileGroupSearchFilter((prev) => ({ ...prev, ...{ filter: tempSearchString, pageIndex: 0 } }));
      } else {
        setMemberProfileGroupSearchFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
    }
  }, [isMounted, searchInitialized, setMemberProfileGroupSearchFilter, tempSearchString]);

  return (
    <GridWrapper>
      <Grid item xs={12}>
        <Paper>
          <PageFilterSection
            leftContent={
              <SearchField
                initialValue={searchFilter.filter || ''}
                onSearch={(newSearchString) => {
                  setSearchInitialized(true);
                  setTempSearchString(newSearchString);
                }}
              />
            }
            rightContent={
              <Grid
                item
                xs='auto'
                sx={{
                  py: 2,
                  pl: 2,
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'flex-end',
                  '& .MuiAutocomplete-inputRoot': {
                    backgroundColor: 'white',
                  },
                }}
              >
                {currentUser ? (
                  <AssociationAndDistrictSelect
                    initialDistrictId={memberProfileGroupSearchFilter.districtId || undefined}
                    initialAssociationId={memberProfileGroupSearchFilter.associationId || undefined}
                    hideDistrict={currentUser?.districts.length === 0}
                    association={association}
                    district={district}
                    associationCallback={(val) => {
                      setAssociation(val || undefined);
                      setMemberProfileGroupSearchFilter((prev) => ({
                        ...prev,
                        associationId: val?.localAss_id,
                      }));
                    }}
                    districtCallback={(val) => {
                      setDistrict(val || undefined);
                      setMemberProfileGroupSearchFilter((prev) => ({
                        ...prev,
                        districtId: val?.countyAss_id,
                      }));
                    }}
                  />
                ) : (
                  <Skeleton sx={{ width: '25vw' }} />
                )}
              </Grid>
            }
          />
          <MemberProfileGroupDataGrid
            loadingCallback={(x) => ConsoleHelper.log(x)}
            searchFilter={searchFilter}
            searchFilterCallback={(newFilter) =>
              setMemberProfileGroupSearchFilter((prev) => ({
                ...prev,
                ...newFilter,
              }))
            }
          />
        </Paper>
      </Grid>
    </GridWrapper>
  );
};
